/**
 * ENG-4580: pane-scrape parser for Claude Code's `/login` OAuth flow.
 *
 * The manager drives the flow by sending `/login` into the agent's
 * persistent tmux session, capturing the pane after a short poll, and
 * extracting the OAuth URL Claude Code prints. After the operator
 * pastes the auth code via the UI, the manager sends it back into the
 * pane and polls for a success / failure marker.
 *
 * Everything in this module is pure — no tmux calls, no fs I/O. The
 * runtime side (apps/cli/src/lib/manager-worker.ts) shells out and
 * feeds the captured pane string through these functions.
 *
 * Why a dedicated module: pane scraping is fragile across Claude Code
 * versions, terminal widths, and locale changes. Centralising the
 * regexes + the ANSI stripper makes them easy to fixture-test and
 * iterate on without touching the runtime path.
 */

// ---------------------------------------------------------------------------
// ANSI escape sequence stripper
// ---------------------------------------------------------------------------

/**
 * Strip the ANSI escape sequences a terminal emits for colour, cursor
 * movement, screen clears, and bracketed paste mode. The pattern below
 * covers:
 *
 *   - CSI sequences: `ESC [ ... <final byte>` where the final byte is
 *     in the 0x40-0x7E range (covers SGR colour, cursor-position,
 *     erase-in-line/display, etc.)
 *   - OSC sequences: `ESC ] ... BEL` or `ESC ] ... ESC \` (used for
 *     window titles and hyperlinks)
 *   - Single-character `ESC <char>` two-byte escapes (e.g. `ESC =`,
 *     `ESC >`, the `ESC c` reset)
 *
 * We keep newlines and printable text intact so pane content remains
 * matchable after stripping.
 *
 * The regex uses Unicode-friendly character classes; we explicitly
 * avoid `\x1b` named escapes in source to keep the file ASCII-safe.
 */
const ANSI_ESC = String.fromCharCode(0x1b);
const ANSI_BEL = String.fromCharCode(0x07);

const CSI_RE = new RegExp(`${ANSI_ESC}\\[[0-?]*[ -/]*[@-~]`, 'g');
const OSC_RE = new RegExp(
  `${ANSI_ESC}\\][^${ANSI_BEL}${ANSI_ESC}]*(?:${ANSI_BEL}|${ANSI_ESC}\\\\)`,
  'g',
);
const TWO_BYTE_RE = new RegExp(`${ANSI_ESC}[=>cM78]`, 'g');

export function stripAnsi(text: string): string {
  return text.replace(CSI_RE, '').replace(OSC_RE, '').replace(TWO_BYTE_RE, '');
}

// ---------------------------------------------------------------------------
// OAuth URL extraction
// ---------------------------------------------------------------------------

/**
 * Anchored to Anthropic-owned domains that Claude Code's `/login`
 * actually prints. Adding more hosts is fine — keep them allowlisted
 * rather than matching arbitrary `https://` to avoid pulling random
 * URLs from the user's previous shell output.
 *
 * Note: Claude Code currently emits URLs at `claude.com/cai/oauth/...`
 * (the consumer-facing domain), but `claude.ai` and the console hosts
 * have appeared historically and are kept here for tolerance.
 */
const OAUTH_URL_RE =
  /https:\/\/(?:claude\.com|claude\.ai|platform\.claude\.com|console\.anthropic\.com|auth\.anthropic\.com)\/[^\s)\]]*/;

/**
 * Strip ANSI + reassemble URLs that were soft-wrapped across terminal
 * lines. tmux capture-pane emits hard newlines for wrapped lines, and
 * Claude Code's OAuth URL routinely runs >300 chars — much wider than
 * the manager's default tmux window. Iteratively strip newlines that
 * fall inside what looks like a URL until convergence (a single URL
 * can wrap 5+ times).
 *
 * Shared between extractOAuthUrl and isUrlPromptReady so the readiness
 * check sees the same string the extractor would.
 */
function dewrapPane(rawPane: string): string {
  const stripped = stripAnsi(rawPane);
  let dewrapped = stripped;
  let prev = '';
  while (prev !== dewrapped) {
    prev = dewrapped;
    dewrapped = dewrapped.replace(/(https?:\/\/\S+?)\n(?=\S)/, (_m, head: string) => head);
  }
  return dewrapped;
}

export function extractOAuthUrl(rawPane: string): string | null {
  const match = OAUTH_URL_RE.exec(dewrapPane(rawPane));
  if (!match) return null;
  // Trim trailing punctuation that often clings to URLs in TUIs.
  return match[0].replace(/[.,;:!?]+$/, '');
}

// ---------------------------------------------------------------------------
// Prompt readiness — "we've printed the URL, now waiting for a code"
// ---------------------------------------------------------------------------

const URL_PROMPT_RE =
  /(?:Paste code here|Paste your code|Enter (?:the )?code|Authorization code)/i;

export function isUrlPromptReady(rawPane: string): boolean {
  const dewrapped = dewrapPane(rawPane);
  // Both anchors must be present: the URL itself AND the paste-code
  // prompt. The prompt alone could appear during a stale screen redraw;
  // the URL alone could be a stray match in command history. Use the
  // dewrapped pane so a wrapped URL still matches OAUTH_URL_RE.
  return OAUTH_URL_RE.test(dewrapped) && URL_PROMPT_RE.test(dewrapped);
}

// ---------------------------------------------------------------------------
// Outcome detection after submitting the code
// ---------------------------------------------------------------------------

// Claude Code's success/failure copy has drifted across versions
// ("Logged in" → "Login successful" → "Signed in as ..." → etc.).
// Keep the alternations broad-but-specific — phrases that only appear
// after a real auth roundtrip, never in welcome / tutorial text.
// "Welcome back" was tried and rejected: it shows up in onboarding
// help blurbs and produced false positives.
const SUCCESS_RE =
  /(?:Logged in|Login successful|Successfully (?:logged in|authenticated|signed in)|Authentication successful|Sign-?in (?:complete|successful)|You(?:'|’)?re signed in|Signed in as)/i;
const FAILURE_RE =
  /(?:Invalid (?:code|authorization code)|OAuth error|Authentication failed|Error (?:logging in|during authentication)|Login failed|Sign-?in failed|Failed to (?:authenticate|sign in|log in))/i;

export type AuthOutcome =
  | { kind: 'success'; rawMatch: string }
  | { kind: 'failure'; rawMatch: string }
  | { kind: 'pending' };

export function detectAuthOutcome(rawPane: string): AuthOutcome {
  const stripped = stripAnsi(rawPane);
  // Failure first — Claude Code sometimes prints a stale "logged in" from
  // a previous successful session above the new failure banner. The
  // most-recent line wins, so we scan from the end of the pane.
  const failureMatch = lastMatch(stripped, FAILURE_RE);
  const successMatch = lastMatch(stripped, SUCCESS_RE);

  if (failureMatch && successMatch) {
    // Whichever is later on the pane is the live state.
    if (failureMatch.index > successMatch.index) {
      return { kind: 'failure', rawMatch: failureMatch.match };
    }
    return { kind: 'success', rawMatch: successMatch.match };
  }
  if (failureMatch) return { kind: 'failure', rawMatch: failureMatch.match };
  if (successMatch) return { kind: 'success', rawMatch: successMatch.match };
  return { kind: 'pending' };
}

function lastMatch(haystack: string, re: RegExp): { match: string; index: number } | null {
  // Construct a sticky/global variant if needed. Most of our REs are
  // anchored to small phrases; iterating with a `g`-flagged RegExp is
  // cheap and correct.
  const globalRe = new RegExp(re.source, re.flags.includes('g') ? re.flags : `${re.flags}g`);
  let last: RegExpExecArray | null = null;
  let m: RegExpExecArray | null;
  while ((m = globalRe.exec(haystack)) !== null) {
    last = m;
    // Prevent zero-length matches from looping.
    if (m.index === globalRe.lastIndex) globalRe.lastIndex++;
  }
  return last ? { match: last[0], index: last.index } : null;
}

// ---------------------------------------------------------------------------
// "Session not running / tmux missing" — surface as a structured signal
// ---------------------------------------------------------------------------

/**
 * The runtime path will throw when `tmux capture-pane` fails. This
 * helper classifies the failure for the API layer so the UI can show
 * "start a session first" rather than a generic 500.
 */
export type SessionError =
  | { kind: 'no-session' }
  | { kind: 'tmux-missing' }
  | { kind: 'pane-empty' }
  /** Claude is sitting on the "OAuth error: Invalid code. Press Enter to
   *  retry." prompt from a previous failed login — pressing Enter would
   *  resubmit the bad code, so the runtime bails with operator-actionable
   *  guidance instead. Distinct from `unknown` so telemetry / UI can
   *  surface a specific message rather than a generic 500. */
  | { kind: 'oauth-retry-stuck'; message: string }
  /** ENG-10617: the pair `claude` process exited mid-flow. The pair
   *  session runs with `remain-on-exit on`, so the dead pane survives and
   *  `message` carries claude's exit status and last output — the only
   *  record of WHY it exited. */
  | { kind: 'claude-exited'; message: string }
  | { kind: 'unknown'; message: string };

export function classifyTmuxError(err: unknown): SessionError {
  const msg = err instanceof Error ? err.message : String(err);
  // ENG-10617: tmux 3.x phrases a `-t` miss by the target type it resolved
  // — `can't find pane` from capture-pane/send-keys, `can't find window` from
  // window-scoped commands — not only `can't find session`. Unmatched, those
  // fell to `unknown` and the raw command line reached the operator.
  if (/can't find (session|pane|window)|no server running/i.test(msg)) return { kind: 'no-session' };
  if (/command not found.*tmux|ENOENT.*tmux/i.test(msg)) return { kind: 'tmux-missing' };
  return { kind: 'unknown', message: msg };
}
